// Comprehensive Pie Chart Test Data
// This file contains all edge cases for pie chart testing

export const pieChartTestData = {
  // Basic comprehensive test data with mixed label lengths
  comprehensive: {
    name: "Comprehensive Pie Chart Test Data",
    description: "Mixed label lengths and various values",
    data: [
      { display: "A", stack1: 25 },
      { display: "Very Long Category Name That Should Test Text Wrapping and Overflow Handling in Pie Chart Legends", stack1: 15 },
      { display: "Medium Length Category", stack1: 20 },
      { display: "Short", stack1: 10 },
      { display: "Another Very Long Category Name That Exceeds Normal Display Limits and Tests UI Responsiveness", stack1: 8 },
      { display: "B", stack1: 12 },
      { display: "Normal Category", stack1: 18 },
      { display: "Extremely Long Category Name That Should Definitely Test the Limits of Text Display and Legend Positioning", stack1: 5 },
      { display: "C", stack1: 7 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Single data point
  singleDataPoint: {
    name: "Single Data Point",
    description: "Single data point for testing edge cases",
    data: [
      { display: "Only Category", stack1: 100 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Large dataset (100+ items) for performance testing
  largeDataset: {
    name: "Large Dataset (100+ items)",
    description: "Large dataset for testing performance and rendering",
    data: Array.from({ length: 100 }, (_, i) => ({
      display: `Category ${i + 1}`,
      stack1: Math.floor(Math.random() * 100) + 1
    })),
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Very short labels
  shortLabels: {
    name: "Short Labels",
    description: "Dataset with very short category names",
    data: [
      { display: "A", stack1: 30 },
      { display: "B", stack1: 25 },
      { display: "C", stack1: 20 },
      { display: "D", stack1: 15 },
      { display: "E", stack1: 10 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Very long labels
  longLabels: {
    name: "Long Labels",
    description: "Dataset with very long category names",
    data: [
      {
        display: "Users Who Completed the Entire Onboarding Process Including Email Verification and Profile Setup",
        stack1: 60
      },
      {
        display: "Users Who Made Their First Purchase Within 24 Hours of Registration and Left a Review",
        stack1: 35
      },
      {
        display: "Power Users Who Created More Than 10 Posts and Engaged with Community Features Daily",
        stack1: 15
      },
      {
        display: "Premium Subscribers Who Upgraded Within the First Week and Enabled All Notification Settings",
        stack1: 8
      }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Mixed label lengths
  mixedLabels: {
    name: "Mixed Label Lengths",
    description: "Mix of short, medium, and long labels",
    data: [
      { display: "A", stack1: 20 },
      { display: "Short Label", stack1: 15 },
      { display: "Medium Length Category Name", stack1: 25 },
      { display: "Very Long Category Name That Tests Text Wrapping and Legend Display", stack1: 18 },
      { display: "B", stack1: 12 },
      { display: "Another Medium Category", stack1: 10 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Very small values for precision testing
  smallValues: {
    name: "Small Values",
    description: "Dataset with very small values to test precision",
    data: [
      { display: "Tiny Value", stack1: 0.1 },
      { display: "Small Value", stack1: 0.5 },
      { display: "Medium Value", stack1: 1.2 },
      { display: "Large Value", stack1: 98.2 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Zero and negative values
  edgeValues: {
    name: "Edge Values",
    description: "Dataset with zero and negative values",
    data: [
      { display: "Zero Value", stack1: 0 },
      { display: "Negative Value", stack1: -5 },
      { display: "Positive Value", stack1: 105 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Sparse data with some zero values
  sparseData: {
    name: "Sparse Data",
    description: "Dataset with some zero/missing values",
    data: [
      { display: "Category A", stack1: 100 },
      { display: "Category B", stack1: 0 },
      { display: "Category C", stack1: 30 },
      { display: "Category D", stack1: 0 },
      { display: "Category E", stack1: 70 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Multi-stack data converted to pie chart format (like props1.js structure)
  multiStack: {
    name: "Multi-Stack Data (Pie Chart Format)",
    description: "Data converted from props1.js structure for pie chart",
    data: [
      {
        display: "Logged In Once",
        stack1: 45
      },
      {
        display: "Registered Users", 
        stack1: 92
      },
      {
        display: "Created Profile",
        stack1: 33
      },
      {
        display: "Wrote at least 3 story",
        stack1: 18
      },
      {
        display: "Created Profile again",
        stack1: 44
      },
      {
        display: "Wrote at least 1 story",
        stack1: 57
      }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Extreme edge cases
  extremeCases: {
    name: "Extreme Edge Cases",
    description: "Extreme edge cases for testing",
    data: [
      { display: "A", stack1: 0.001 },
      { display: "Very Long Category Name That Should Test Text Wrapping and Overflow Handling in Pie Chart Legends and Tooltips", stack1: 0.002 },
      { display: "B", stack1: 99.997 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Count'
    },
    labelPath: "display",
    breakdownCategoryName: "Categories"
  },

  // Real-world scenario data
  realWorld: {
    name: "Real World Scenario",
    description: "Real-world data scenario",
    data: [
      { display: "Desktop Users", stack1: 45 },
      { display: "Mobile Users", stack1: 35 },
      { display: "Tablet Users", stack1: 15 },
      { display: "Other Devices", stack1: 5 }
    ],
    valuePath: "stack1",
    tooltipLabelsMapping: {
      stack1: 'Users'
    },
    labelPath: "display",
    breakdownCategoryName: "Device Types"
  }
};

// Helper function to get all test data names
export const getPieChartTestDataNames = () => {
  return Object.keys(pieChartTestData);
};

// Helper function to get specific test data
export const getPieChartTestData = (name) => {
  return pieChartTestData[name] || null;
};
